/*
 * Iter305 — Session-scoped success-toast gate.
 *
 * The OPS toolbar lets an operator flip "Alerts" OFF to silence the
 * flood of green success/confirmation toasts (player assigned, court
 * updated, sponsor saved, etc.) that stack up in the top-right during
 * a busy festival. Errors and warnings are never gated — a failed
 * assign or a validation error must still surface.
 *
 * Implementation notes:
 *   · Sonner exposes `toast` as a callable + `.success/.info/.message/
 *     .warning/.error/.loading/.promise` properties. We monkey-patch
 *     the whole object once, on first import, so every consumer in
 *     the app benefits without any refactor.
 *   · The gate reads `sessionStorage["rl_ops_alerts_off"]` on EVERY
 *     call so toggling the flag takes effect immediately without a
 *     re-render or reload.
 *   · Only success/info/message/loading are gated. errors + warnings
 *     always fire. Promise toasts still pipe through — sonner routes
 *     them via toast.promise which internally calls toast.success on
 *     resolve, so those are naturally gated too when Alerts=OFF.
 */
import { toast as sonner } from "sonner";

const FLAG = "rl_ops_alerts_off";
const GATED_METHODS = ["success", "info", "message", "loading"];
let installed = false;

export function isAlertsOff() {
  try { return window.sessionStorage.getItem(FLAG) === "1"; } catch { return false; }
}

export function setAlertsOff(off) {
  try {
    if (off) window.sessionStorage.setItem(FLAG, "1");
    else window.sessionStorage.removeItem(FLAG);
  } catch {}
}

export function installOpsToastGate() {
  if (installed) return;
  installed = true;
  for (const m of GATED_METHODS) {
    const orig = sonner[m];
    if (typeof orig !== "function") continue;
    sonner[m] = (...args) => {
      if (isAlertsOff()) return undefined;
      return orig.apply(sonner, args);
    };
  }
}
